import Info from '@clearkit/icons/glyphs/Info';
import classnames from 'classnames';
import { CKButton, CKIconStack } from 'clearkit';

import Logo from '~/components/ConfigurationLogo';
import { buildAccountNotification } from '~/lib/accountNotifications';
import { purposeForConnectorService } from '~/lib/configuration';
import { readableType } from '~/lib/dataEnrichment';
import history from '~/lib/history';
import paths from '~/lib/paths';

import styles from './styles.js';

function AccountNotification({
  classes,
  accountNotification,
  className,
  icon,
  showResolveButton,
  fixIssue,
  isFromEnrichmentPage,
}) {
  const {
    sourceName,
    notificationType,
    message,
    segmentType,
    segmentId,
    sourceId,
  } = accountNotification;

  let resolve = fixIssue;

  const {
    isAccountConnectorNotification,
    isDestinationNotification,
    shouldRenderNotification,
    documentationLink,
    helpText,
    messageDescription,
    isEnrichmentNotification,
  } = buildAccountNotification(
    message,
    notificationType,
    readableType(sourceName),
  );

  if (isDestinationNotification) {
    resolve = () =>
      history.push(
        paths.segmentDestinationEdit(segmentType, segmentId, sourceId),
      );
  } else if (documentationLink) {
    resolve = () => window.open(documentationLink);
  }

  if (!shouldRenderNotification(isFromEnrichmentPage)) {
    return null;
  }

  return (
    <div
      className={classnames(
        classes.notification,
        'w-full px-[20px] py-[10px] flex items-center bg-yellow-50 border-yellow-200 border border-solid justify-between shadow flex-row',
        className,
      )}
      data-testid="account-notification-container">
      <div className="flex items-center mr-4">
        {(() => {
          if (icon) {
            return icon;
          }

          if (isAccountConnectorNotification || isDestinationNotification) {
            return (
              <CKIconStack
                base={<Logo type={sourceName} />}
                badge={<Info className="fill-gradient-br-yellow" />}
              />
            );
          }

          return (
            <Info className="fill-gradient-br-yellow" width={26} height={26} />
          );
        })()}
      </div>
      <div className="flex flex-col items-start flex-1">
        <span
          className="block text-sm font-medium leading-tight text-yellow-800"
          data-testid={`notification-description-${notificationType}`}>
          {isEnrichmentNotification
            ? messageDescription
            : messageDescription ||
              `There was an error ${purposeForConnectorService(sourceName)}`}
        </span>
        {(() => {
          if (isEnrichmentNotification) {
            return (
              <span className="text-sm text-yellow-800">
                Please verify that the Clearbit integration user has permissions
                to read/write fields in {readableType(sourceName)}.
              </span>
            );
          }

          if (
            (!isAccountConnectorNotification && !isDestinationNotification) ||
            !sourceName
          ) {
            return null;
          }

          const action =
            notificationType === 'account_connector_auth'
              ? 'Reconnect'
              : 'Resolve';

          return (
            <span className="text-sm text-yellow-800">
              {action} to continue {purposeForConnectorService(sourceName)}
            </span>
          );
        })()}
      </div>
      <div className="ml-4">
        {/*
         THIS IS MEANT TO BE A TEMPORARY SOLUTION! PLEASE, YOU SHOULD NOT BE
         ADDING ANYTHING HERE! INSTEAD, REFACTOR IT.
      */}
        {showResolveButton && !isEnrichmentNotification && (
          <CKButton
            variantColor="yellow"
            variant="bold"
            onClick={resolve}
            data-testid="fix-issue-button">
            {helpText}
          </CKButton>
        )}
      </div>
    </div>
  );
}

AccountNotification.defaultProps = {
  fixIssue: () => history.push(paths.configurations()),
  showResolveButton: true,
  isFromEnrichmentPage: false,
};

AccountNotification.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  isFromEnrichmentPage: PropTypes.bool,
  accountNotification: PropTypes.object.isRequired,
  icon: PropTypes.node,
  fixIssue: PropTypes.func,
  showResolveButton: PropTypes.bool,
};

export default withStyles(styles)(AccountNotification);
